import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import { PACKAGE_ACTIVATION_ROUTE } from '../routes/routes';
import { useLazyGetBookingIntentsByVinQuery } from '../api/bookingIntent/bookingIntentApiSlice';
import type { BookingIntent, BookingIntentAsset } from '../api/bookingIntent/types';

interface HeaderItem {
    id: string;
    label: string;
    description?: string;
}

const HEADERS: readonly HeaderItem[] = [
    {
        id: 'vin',
        label: 'VIN',
    },
    {
        id: 'asset-id',
        label: 'Asset ID',
    },
    {
        id: 'account-id',
        label: 'Account ID',
    },
    {
        id: 'operation-number',
        label: 'Operation Number',
        description:
            'Operation number associated with the VMS system or the one manually created via Create Booking Intent button',
    },
    {
        id: 'package-code',
        label: 'Package Code',
        description: 'Configuration code representing the package.',
    },
    {
        id: 'status',
        label: 'Status',
        description: 'Flag indicating process status for the booking intent: Delivered, Invoiced, Canceled, Reversed',
    },
    {
        id: 'country',
        label: 'Country',
        description: 'Country code of the Sold-to party.',
    },
    {
        id: 'invoiced-at',
        label: 'Invoiced At',
        description:
            'Date when the vehicle is invoiced to the end customer. This is the date used as start date for the activations',
    },
] as const;

const formatDateValue = (dateString?: string, dateFormat = 'dd.MM.yyyy') => {
    if (!dateString) {
        return '';
    }

    try {
        return format(new Date(dateString), dateFormat, { locale: de });
    } catch {
        return dateString;
    }
};

const renderLinkedValue = (value: string | undefined, queryParam: 'assetId' | 'accountId') => {
    if (!value) {
        return '-';
    }

    return <a href={`/${PACKAGE_ACTIVATION_ROUTE}?${queryParam}=${encodeURIComponent(value)}`}>{value}</a>;
};
const createAssetKey = (bookingIntent: BookingIntent, asset: BookingIntentAsset) =>
    `${bookingIntent.operationNumber}-${asset.accountId ?? 'no-account'}-${asset.assetId ?? 'no-asset'}`;

interface BookingIntentTableProps {
    onCreateBookingIntent?: () => void;
}

export const BookingIntentTable = ({ onCreateBookingIntent }: BookingIntentTableProps) => {
    const [vin, setVin] = useState('');
    const [triggerGetBookingIntents, { currentData: bookingIntents = [], isFetching, isError }] =
        useLazyGetBookingIntentsByVinQuery();
    const normalizedVin = vin.trim();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setVin(params.get('vin') ?? '');
    }, []);

    useEffect(() => {
        if (!normalizedVin) {
            return;
        }

        void triggerGetBookingIntents(normalizedVin);
    }, [normalizedVin, triggerGetBookingIntents]);

    useEffect(() => {
        const params = new URLSearchParams();

        if (normalizedVin) {
            params.set('vin', normalizedVin);
        }

        const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
        window.history.pushState({}, '', newUrl);
    }, [normalizedVin]);

    return (
        <>
            <div className='table-toolbar display-flex justify-content-center'>
                <div className='table-toolbar-container'>
                    <div className='table-toolbar-group-left'>
                        <div className='table-toolbar-column'>
                            <label className='table-toolbar-label'>VIN</label>
                            <div className='table-toolbar-search input-group'>
                                <span className='input-group-addon'>
                                    <span className='rioglyph rioglyph-search' />
                                </span>
                                <div className='ClearableInput input-group'>
                                    <input
                                        type='text'
                                        placeholder='Search vin...'
                                        className='form-control'
                                        value={vin}
                                        onChange={event => setVin(event.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='table-toolbar-group-right'>
                        <div className='table-toolbar-column'>
                            <button type='button' className='btn btn-primary' onClick={onCreateBookingIntent}>
                                <span className='rioglyph rioglyph-plus-light' aria-hidden='true' />
                                <span className='text-capitalize'>Create booking intent</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className='table-responsive min-height-350'>
                <table className='table table-hover table-head-filled table-bordered'>
                    <thead>
                        <tr>
                            {HEADERS.map(header => (
                                <th key={header.id}>
                                    <span className='display-inline-flex align-items-center gap-5'>
                                        <span>{header.label}</span>
                                        {header.description && (
                                            <OverlayTrigger
                                                placement='top'
                                                trigger='hover'
                                                overlay={
                                                    <Tooltip id={`booking-intent-header-tooltip-${header.id}`}>
                                                        {header.description}
                                                    </Tooltip>
                                                }
                                            >
                                                <span
                                                    className='rioglyph rioglyph-info-sign text-muted text-size-12'
                                                    data-testid={`booking-intent-header-info-${header.id}`}
                                                    aria-hidden='true'
                                                />
                                            </OverlayTrigger>
                                        )}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {!normalizedVin && (
                            <tr>
                                <td colSpan={HEADERS.length} className='text-center'>
                                    Enter a VIN to search for booking intents.
                                </td>
                            </tr>
                        )}
                        {normalizedVin && isFetching && (
                            <tr>
                                <td colSpan={HEADERS.length} className='text-center'>
                                    Loading booking intents...
                                </td>
                            </tr>
                        )}
                        {normalizedVin && !isFetching && isError && (
                            <tr>
                                <td colSpan={HEADERS.length} className='text-center'>
                                    Failed to load booking intents.
                                </td>
                            </tr>
                        )}
                        {normalizedVin && !isFetching && !isError && bookingIntents.length === 0 && (
                            <tr>
                                <td colSpan={HEADERS.length} className='text-center'>
                                    No booking intents found for this VIN.
                                </td>
                            </tr>
                        )}
                        {normalizedVin &&
                            bookingIntents.map(bookingIntent => (
                                <tr key={bookingIntent.operationNumber}>
                                    <td>{bookingIntent.vin}</td>
                                    <td>
                                        {bookingIntent.assets.length > 0
                                            ? bookingIntent.assets.map(asset => (
                                                  <div key={createAssetKey(bookingIntent, asset)}>
                                                      {renderLinkedValue(asset.assetId, 'assetId')}
                                                  </div>
                                              ))
                                            : '-'}
                                    </td>
                                    <td>
                                        {bookingIntent.assets.length > 0
                                            ? bookingIntent.assets.map(asset => (
                                                  <div key={createAssetKey(bookingIntent, asset)}>
                                                      {renderLinkedValue(asset.accountId, 'accountId')}
                                                  </div>
                                              ))
                                            : '-'}
                                    </td>
                                    <td>{bookingIntent.operationNumber}</td>
                                    <td>{bookingIntent.packageCode}</td>
                                    <td>{bookingIntent.status}</td>
                                    <td>{bookingIntent.soldToPartyCountryCode}</td>
                                    <td>{formatDateValue(bookingIntent.invoicedAt)}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export const BookingIntentTableContainer = BookingIntentTable;
